import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output, ViewChild, computed, inject, signal } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import {
  BUSINESS_CONTEXT_CHANNEL,
  BUSINESS_CONTEXT_QUERY_PARAM,
  BusinessContextParentMessage,
  businessContextWindowName,
  isBusinessContextMessage,
} from '../../utils/business-context';

interface ContextInfo {
  name?: string;
  status?: boolean;
  subscription_exempt?: boolean;
  subscription?: { plan?: string; status?: string } | null;
  session_user?: { user_id?: number; name?: string; email?: string } | null;
  business_context?: {
    is_archived?: boolean;
    is_active?: boolean;
    owner_user_id?: number | null;
    owner_name?: string | null;
    owner_email?: string | null;
    owner_is_active?: boolean | null;
  };
}

const PLAN_LABELS: Record<string, string> = { gratis: 'Prueba', basico: 'Plan Básico', estandar: 'Plan Estándar', premium: 'Plan Premium' };
const STATUS_LABELS: Record<string, string> = { active: 'Activa', pending: 'Pendiente', trial: 'En prueba', cancelled: 'Cancelada', expired: 'Vencida', inactive: 'Inactiva' };

/**
 * "Gestionar negocio": muestra el panel completo de un negocio, exactamente
 * como lo ve su propietario, dentro de un iframe de esta misma aplicación.
 * Esta ventana le presta al iframe el token de la sesión del super admin
 * (ver utils/business-context.ts); el iframe nunca toca la cookie de sesión.
 */
@Component({
  selector: 'app-business-context-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bc-overlay" role="dialog" aria-modal="true" aria-labelledby="bc-title">
      <section class="bc-shell">
        <header class="bc-head">
          <div class="bc-identity">
            <span class="bc-avatar" aria-hidden="true">{{ initial() }}</span>
            <div class="bc-copy">
              <span class="bc-eyebrow"><i class="bi bi-shield-check" aria-hidden="true"></i> Gestionando negocio</span>
              <h2 id="bc-title">{{ displayName() }}</h2>
              <p>{{ ownerLine() }}</p>
            </div>
          </div>
          <div class="bc-chips" *ngIf="info() as i">
            <span class="bc-chip" [class.tone-success]="isActive()" [class.tone-danger]="!isActive()"><i class="bi" [class.bi-check-circle]="isActive()" [class.bi-pause-circle]="!isActive()" aria-hidden="true"></i>{{ isActive() ? 'Negocio activo' : 'Negocio desactivado' }}</span>
            <span class="bc-chip tone-danger" *ngIf="i.business_context?.is_archived"><i class="bi bi-archive" aria-hidden="true"></i>Dado de baja</span>
            <span class="bc-chip" *ngIf="planLabel() as plan" [class.tone-warning]="planNeedsAttention()"><i class="bi bi-gem" aria-hidden="true"></i>{{ plan }}</span>
          </div>
          <div class="bc-actions">
            <button type="button" class="bc-icon-btn" title="Gestionar la cuenta del propietario" aria-label="Gestionar la cuenta del propietario" *ngIf="ownerUserId()" (click)="manageOwner.emit(ownerUserId()!)"><i class="bi bi-person-gear" aria-hidden="true"></i></button>
            <button type="button" class="bc-icon-btn" title="Recargar el panel del negocio" aria-label="Recargar el panel del negocio" (click)="reload()"><i class="bi bi-arrow-clockwise" aria-hidden="true"></i></button>
            <button type="button" class="bc-close" (click)="close()"><i class="bi bi-x-lg" aria-hidden="true"></i><span>Cerrar</span></button>
          </div>
        </header>
        <p class="bc-notice"><i class="bi bi-info-circle" aria-hidden="true"></i><span>Ves y operas el panel tal como lo ve el propietario. Los cambios se guardan directamente en el negocio y este los verá como realizados por <strong>Equipo TuSala</strong>.</span></p>
        <div class="bc-frame-wrap">
          <div #frameHost class="bc-frame-host" [class.is-hidden]="state() !== 'ready'"></div>
          <div class="bc-state" *ngIf="state() === 'loading'" role="status">
            <span class="bc-spinner" aria-hidden="true"></span>
            <strong>Abriendo el panel de {{ displayName() }}…</strong>
            <span>Cargando la información del negocio.</span>
          </div>
          <div class="bc-state bc-error" *ngIf="state() === 'error'" role="alert">
            <span class="bc-error-icon" aria-hidden="true"><i class="bi bi-exclamation-octagon"></i></span>
            <strong>No se pudo abrir el negocio</strong>
            <span>{{ errorMessage() }}</span>
            <div class="bc-state-actions">
              <button type="button" class="bc-secondary" (click)="close()">Cerrar</button>
              <button type="button" class="bc-primary" (click)="reload()"><i class="bi bi-arrow-clockwise" aria-hidden="true"></i> Reintentar</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    :host{position:fixed;inset:0;z-index:9500;display:block}
    .bc-overlay{position:absolute;inset:0;display:flex;padding:clamp(0px,1.6vw,18px);background:var(--ts-bg-navy-950-a76-2,rgba(2,8,20,.72));backdrop-filter:blur(6px);animation:bcFade .18s ease-out}
    .bc-shell{display:flex;flex-direction:column;width:100%;max-width:1680px;min-height:0;margin:0 auto;overflow:hidden;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:18px;background:var(--ts-bg);box-shadow:var(--ts-shadow-floating,var(--ts-shadow-lg));animation:bcRise .24s cubic-bezier(.2,.8,.2,1)}
    .bc-head{display:flex;align-items:center;gap:1rem;padding:.75rem 1rem;border-bottom:1px solid var(--ts-border-strong);background:linear-gradient(180deg,var(--ts-bg-elevated),var(--ts-surface))}
    .bc-identity{display:flex;align-items:center;gap:.75rem;min-width:0;flex:1 1 auto}
    .bc-avatar{display:grid;place-items:center;flex:0 0 42px;width:42px;height:42px;border-radius:12px;color:var(--ts-on-primary,#fff);background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo));font-weight:800;font-size:1.05rem;text-transform:uppercase}
    .bc-copy{min-width:0}
    .bc-eyebrow{display:inline-flex;align-items:center;gap:.3rem;color:var(--ts-warning);font-size:.62rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
    .bc-copy h2{margin:.1rem 0 0;overflow:hidden;font-size:1.05rem;line-height:1.25;text-overflow:ellipsis;white-space:nowrap}
    .bc-copy p{margin:.1rem 0 0;overflow:hidden;color:var(--ts-text-muted);font-size:.74rem;text-overflow:ellipsis;white-space:nowrap}
    .bc-chips{display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-end;gap:.4rem;min-width:0}
    .bc-chip{display:inline-flex;align-items:center;gap:.32rem;padding:.3rem .6rem;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:999px;background:var(--ts-surface-inset,var(--ts-surface));font-size:.68rem;font-weight:750;white-space:nowrap}
    .bc-chip.tone-success{color:var(--ts-success);border-color:color-mix(in srgb,var(--ts-success) 40%,transparent);background:color-mix(in srgb,var(--ts-success) 11%,transparent)}
    .bc-chip.tone-danger{color:var(--ts-danger);border-color:color-mix(in srgb,var(--ts-danger) 40%,transparent);background:color-mix(in srgb,var(--ts-danger) 11%,transparent)}
    .bc-chip.tone-warning{color:var(--ts-warning);border-color:color-mix(in srgb,var(--ts-warning) 40%,transparent);background:color-mix(in srgb,var(--ts-warning) 11%,transparent)}
    .bc-actions{display:flex;align-items:center;gap:.45rem;flex:0 0 auto}
    .bc-icon-btn,.bc-close{display:inline-grid;place-items:center;height:38px;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:10px;background:var(--ts-surface);cursor:pointer;transition:color .15s ease,border-color .15s ease,background .15s ease}
    .bc-icon-btn{width:38px}
    .bc-close{display:inline-flex;align-items:center;gap:.4rem;padding:0 .9rem;color:var(--ts-text);font-weight:750;font-size:.8rem}
    .bc-icon-btn:hover,.bc-close:hover{color:var(--ts-text);border-color:var(--ts-primary);background:var(--ts-surface-hover)}
    .bc-icon-btn:focus-visible,.bc-close:focus-visible,.bc-primary:focus-visible,.bc-secondary:focus-visible{outline:3px solid var(--ts-focus-ring);outline-offset:2px}
    .bc-notice{display:flex;align-items:center;gap:.5rem;margin:0;padding:.45rem 1rem;color:var(--ts-text-muted);border-bottom:1px solid var(--ts-border);background:color-mix(in srgb,var(--ts-warning) 7%,var(--ts-bg-elevated));font-size:.72rem;line-height:1.45}
    .bc-notice i{flex:0 0 auto;color:var(--ts-warning)}
    .bc-notice strong{color:var(--ts-text)}
    .bc-frame-wrap{position:relative;flex:1 1 auto;min-height:0;background:var(--ts-bg)}
    .bc-frame-host{position:absolute;inset:0}
    .bc-frame-host.is-hidden{visibility:hidden}
    .bc-frame-host iframe{display:block;width:100%;height:100%;border:0;background:var(--ts-bg)}
    .bc-state{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.45rem;padding:1.5rem;text-align:center;background:var(--ts-bg)}
    .bc-state strong{font-size:1rem}
    .bc-state span{max-width:440px;color:var(--ts-text-muted);font-size:.8rem;line-height:1.5}
    .bc-spinner{width:38px;height:38px;margin-bottom:.4rem;border:3px solid var(--ts-border-strong);border-top-color:var(--ts-primary);border-radius:50%;animation:bcSpin .8s linear infinite}
    .bc-error-icon{display:grid;place-items:center;width:54px;height:54px;margin-bottom:.3rem;border-radius:16px;color:var(--ts-danger);background:color-mix(in srgb,var(--ts-danger) 13%,transparent);font-size:1.5rem}
    .bc-state-actions{display:flex;flex-wrap:wrap;justify-content:center;gap:.6rem;margin-top:.6rem}
    .bc-primary,.bc-secondary{display:inline-flex;align-items:center;gap:.4rem;min-height:40px;padding:0 1.1rem;border-radius:10px;font-weight:750;font-size:.82rem;cursor:pointer}
    .bc-primary{color:var(--ts-on-primary,#fff);border:0;background:linear-gradient(135deg,var(--ts-primary),var(--ts-primary-strong,var(--ts-primary)))}
    .bc-secondary{color:var(--ts-text);border:1px solid var(--ts-border-strong);background:var(--ts-surface)}
    @keyframes bcFade{from{opacity:0}to{opacity:1}}
    @keyframes bcRise{from{opacity:0;transform:translateY(10px) scale(.995)}to{opacity:1;transform:none}}
    @keyframes bcSpin{to{transform:rotate(360deg)}}
    @media(max-width:1100px){.bc-chips{display:none}}
    @media(max-width:700px){
      .bc-overlay{padding:0}
      .bc-shell{border:0;border-radius:0}
      .bc-head{gap:.55rem;padding:.55rem .65rem}
      .bc-avatar{flex-basis:36px;width:36px;height:36px;border-radius:10px;font-size:.92rem}
      .bc-copy h2{font-size:.92rem}
      .bc-copy p{display:none}
      .bc-close span{display:none}
      .bc-close{width:38px;padding:0;justify-content:center}
      .bc-notice{padding:.4rem .65rem;font-size:.66rem}
      .bc-notice span{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
    }
    @media(max-width:380px){.bc-actions{gap:.3rem}.bc-icon-btn,.bc-close{width:34px;height:34px}}
    @media(prefers-reduced-motion:reduce){.bc-overlay,.bc-shell{animation:none}.bc-spinner{animation-duration:2s}}
  `],
})
export class BusinessContextModalComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) businessId!: number;
  @Input() businessName = '';
  @Input() ownerName = '';
  @Output() closed = new EventEmitter<void>();
  /** user_id del propietario: abrir la gestión de su cuenta (contraseña, correo). */
  @Output() manageOwner = new EventEmitter<number>();

  @ViewChild('frameHost', { static: true }) private frameHost!: ElementRef<HTMLDivElement>;
  private frame: HTMLIFrameElement | null = null;

  private readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);
  private readonly messageListener = (event: MessageEvent) => this.onMessage(event);
  private loadTimer: ReturnType<typeof setTimeout> | null = null;

  readonly state = signal<'loading' | 'ready' | 'error'>('loading');
  readonly errorMessage = signal('');
  readonly info = signal<ContextInfo | null>(null);

  readonly displayName = computed(() => this.info()?.name || this.businessName || 'Negocio');
  readonly initial = computed(() => (this.displayName() || 'N').trim().charAt(0) || 'N');
  readonly ownerUserId = computed(() => this.info()?.business_context?.owner_user_id ?? null);
  readonly isActive = computed(() => !!this.info()?.business_context?.is_active);
  readonly ownerLine = computed(() => {
    const ctx = this.info()?.business_context;
    const name = ctx?.owner_name || this.ownerName;
    if (!name) return 'Propietario sin asignar';
    return ctx?.owner_email ? `Propietario: ${name} · ${ctx.owner_email}` : `Propietario: ${name}`;
  });
  readonly planLabel = computed(() => {
    const i = this.info();
    if (!i) return '';
    if (i.subscription_exempt) return 'Sin plan (exento)';
    const sub = i.subscription;
    if (!sub?.plan) return 'Sin suscripción';
    const plan = PLAN_LABELS[sub.plan] || sub.plan;
    return sub.status ? `${plan} · ${STATUS_LABELS[sub.status] || sub.status}` : plan;
  });
  readonly planNeedsAttention = computed(() => {
    const i = this.info();
    if (!i || i.subscription_exempt) return false;
    return !i.subscription || ['expired', 'inactive', 'cancelled', 'pending'].includes(String(i.subscription.status || ''));
  });

  ngAfterViewInit(): void {
    window.addEventListener('message', this.messageListener);
    // El panel del negocio trae su propio asistente (Kubo): mientras el modal
    // está abierto se oculta el de esta ventana para no mostrar dos.
    document.body.classList.add('business-context-open');
    this.load();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
    document.body.classList.remove('business-context-open');
    if (this.loadTimer) clearTimeout(this.loadTimer);
    // Corta el panel embebido (y sus peticiones) al cerrar.
    this.frame?.remove();
    this.frame = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    // Solo si no hay otro modal encima (p. ej. la gestión de la cuenta del propietario).
    if (!document.querySelector('.uae-overlay, .dialog-overlay')) this.close();
  }

  close(): void {
    this.closed.emit();
  }

  reload(): void {
    this.load();
  }

  private load(): void {
    this.state.set('loading');
    this.errorMessage.set('');
    // Un iframe nuevo en cada carga, con su nombre (negocio a gestionar)
    // asignado ANTES de insertarlo: cambiar el nombre de un iframe ya creado
    // no llega a la ventana interna, que lo lee al arrancar.
    this.frame?.remove();
    const frame = document.createElement('iframe');
    frame.name = businessContextWindowName(this.businessId);
    frame.title = 'Panel del negocio';
    // Estilos en línea: un elemento creado por código no recibe los estilos
    // encapsulados del componente.
    frame.style.cssText = 'display:block;width:100%;height:100%;border:0;background:transparent';
    frame.src = new URL(`dashboard?${BUSINESS_CONTEXT_QUERY_PARAM}=${this.businessId}`, document.baseURI).href;
    this.frameHost.nativeElement.appendChild(frame);
    this.frame = frame;
    if (this.loadTimer) clearTimeout(this.loadTimer);
    this.loadTimer = setTimeout(() => {
      if (this.state() === 'loading') this.fail('El panel del negocio tardó demasiado en responder. Revisa tu conexión e inténtalo de nuevo.');
    }, 30000);
  }

  private fail(message: string): void {
    this.errorMessage.set(message);
    this.state.set('error');
  }

  private onMessage(event: MessageEvent): void {
    const frameWindow = this.frame?.contentWindow;
    if (!frameWindow || event.source !== frameWindow || event.origin !== window.location.origin) return;
    if (!isBusinessContextMessage(event.data)) return;
    const data = event.data as any;
    this.zone.run(() => {
      switch (data.type) {
        case 'token-request':
          this.replyToken(Number(data.requestId), !!data.refresh);
          break;
        case 'context-loaded':
          this.info.set((data.business || null) as ContextInfo | null);
          this.state.set('ready');
          break;
        case 'context-failed':
          this.fail(String(data.message || 'No fue posible abrir el negocio.'));
          break;
        case 'session-ended':
          this.fail('Tu sesión de Super Admin terminó. Inicia sesión nuevamente para seguir gestionando negocios.');
          break;
        case 'close':
          this.close();
          break;
        case 'manage-owner-account': {
          const ownerId = this.ownerUserId();
          if (ownerId) this.manageOwner.emit(ownerId);
          break;
        }
      }
    });
  }

  private replyToken(requestId: number, refresh: boolean): void {
    const send = (token: string | null) => {
      const message: BusinessContextParentMessage = { channel: BUSINESS_CONTEXT_CHANNEL, type: 'token', requestId, token };
      this.frame?.contentWindow?.postMessage(message, window.location.origin);
    };
    const current = this.auth.getToken();
    if (current && !refresh) {
      send(current);
      return;
    }
    this.auth.refreshSession().subscribe({
      next: ok => send(ok ? this.auth.getToken() : null),
      error: () => send(null),
    });
  }
}
